import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React from 'react';
import type { DropzoneOptions, FileRejection } from 'react-dropzone';
import { useTranslationContext } from '@/context/translation';
import {
	useEmailFunnelFlow,
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
	useTranslationWithLocale,
	useUploadImage,
} from '@/hooks';
import {
	AmplitudeService,
	CookiesStorageService,
	GoogleService,
} from '@/services';
import { getCurrentLandingPath, isPageLocalized } from '@/helpers';
import { reviewsLanding } from '@/data';
import type { LandingConfigItem } from '@/types';
import {
	AMPLITUDE_EVENTS,
	DEFAULT_LOCALE,
	GOOGLE_EVENTS,
	LANDING_CONSTANTS,
	LANDING_COOKIE_STORAGE,
} from '@/constants';
import { Reviews } from '../reusableSections';
import { Benefits } from './Benefits';
import { EmailPopup } from './EmailPopup';
import { FAQ } from './FAQ';
import { Formats } from './Formats';
import { Hero } from './Hero';
import { HowItWorks } from './HowItWorks';
import { ToolsGrid } from './ToolsGrid';
import { ToolsWidget } from './ToolsWidget';
import { TryLift } from './TryLift';
import { WhyLift } from './WhyLift';

interface LandingTemplateProps {
	data: LandingConfigItem;
	funnelLinkService?: string;
}
export const LandingTemplate: React.FC<LandingTemplateProps> = ({
	data,
	funnelLinkService,
}) => {
	const router = useRouter();
	const { locale, translations } = useTranslationContext();

	const currentPath = getCurrentLandingPath(router);
	const isLocalized = isPageLocalized(currentPath);

	// Use default locale for landing-reviews on non-localized pages
	const reviewsLocale = isLocalized ? locale : DEFAULT_LOCALE;
	const { t: tReviews } = useTranslationWithLocale(
		'landing-reviews',
		reviewsLocale,
		translations?.['landing-reviews'] as Record<string, unknown> | undefined,
	);
	const { t: tToolsGrid } = useTranslation(
		data.toolsGrid?.locale ?? data.translationLocale,
	);
	const { t } = useTranslationWithLocale(
		data.translationLocale,
		locale,
		translations?.[data.translationLocale] as
			| Record<string, unknown>
			| undefined,
	);
	const { t: tShared } = useTranslationWithLocale(
		'sharedLandingData',
		locale,
		translations?.sharedLandingData as Record<string, unknown> | undefined,
	);
	const { t: tCommon } = useTranslationWithLocale('common');

	const { open: openEmailPopup, popupProps } = useEmailFunnelFlow({
		funnelLinkService,
		t,
		tCommon,
	});

	// States
	const [loading, setLoading] = React.useState(false);
	const [referenceImagePreview, setReferenceImagePreview] = React.useState<
		string | null
	>(null);
	const [referenceImageUrl, setReferenceImageUrl] = React.useState<
		string | null
	>(null);

	// Logic
	const uploadImage = useUploadImage();

	// Helpers
	const isLoading = loading || uploadImage.isLoading;
	const isImageGeneratorHero = data.hero.type === 'imageGenerator';

	// Accepted file types: use converter/compressor set (HEIC, etc.) when config says so
	const acceptedTypes =
		data.converterCompressor === true
			? LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE_CONVERTER_COMPRESSOR
			: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE;

	// Actions
	const handleDrop = (
		acceptedFiles: File[],
		fileRejections: FileRejection[],
	) => {
		if (fileRejections.length > 0) {
			return;
		}

		const file = acceptedFiles[0];

		if (isImageGeneratorHero && file) {
			setReferenceImagePreview(URL.createObjectURL(file));
		}

		setLoading(true);
		uploadImage.mutate(
			{ file },
			{
				onSuccess: async ({ viewUrl }) => {
					// A landing with its own key hands the funnel one unambiguous link
					CookiesStorageService.set(
						data.mediaCookieKey ?? LANDING_COOKIE_STORAGE.IMAGE_FROM_LANDING,
						viewUrl,
					);
					GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
					await AmplitudeService.logEventAsync(AMPLITUDE_EVENTS.MEDIA_ADDED, {
						context: data.analytics.galleryContext,
					});
					setLoading(false);

					// The image-generator hero lets the user review the reference image
					// and prompt before generating — don't redirect away immediately.
					if (isImageGeneratorHero) {
						setReferenceImageUrl(viewUrl);
						return;
					}

					setTimeout(() => {
						if (data.requiresEmailGate) {
							openEmailPopup();
							return;
						}
						if (funnelLinkService) {
							window.location.href = funnelLinkService;
						}
					}, 300);
				},
				onError: () => {
					setLoading(false);
					if (isImageGeneratorHero) {
						setReferenceImagePreview(null);
					}
				},
			},
		);
	};

	const handleRemoveReferenceImage = React.useCallback(() => {
		if (referenceImagePreview) {
			URL.revokeObjectURL(referenceImagePreview);
		}
		setReferenceImagePreview(null);
		setReferenceImageUrl(null);
		uploadImage.reset();
	}, [referenceImagePreview, uploadImage]);

	const openGalleryEvent = React.useCallback(() => {
		AmplitudeService.logEvent(AMPLITUDE_EVENTS.MEDIA_GALLERY_SHOWN, {
			context: data.analytics.galleryContext,
		});
	}, [data.analytics.galleryContext]);

	// Analytics
	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		data.analytics.scrolledPercentName,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: data.analytics.eventPropertyType },
	);
	useLandingShownEvent({ type: data.analytics.eventPropertyType });

	const useEmailFlow =
		data.hero.type === 'webLink' || data.requiresEmailGate === true;

	const dropzoneOptions: DropzoneOptions = React.useMemo(
		() => ({
			disabled: isLoading,
			multiple: false,
			accept: acceptedTypes,
			maxFiles: LANDING_CONSTANTS.MAX_FILES,
			maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
			onDrop: handleDrop,
			onFileDialogOpen: openGalleryEvent,
		}),
		[isLoading, acceptedTypes, handleDrop, openGalleryEvent],
	);

	return (
		<>
			<Hero
				data={data.hero}
				funnelLinkService={funnelLinkService}
				t={t}
				isLoading={isLoading}
				dropzoneOptions={dropzoneOptions}
				converterCompressor={data.converterCompressor}
				referenceImagePreview={referenceImagePreview}
				referenceImageUrl={referenceImageUrl}
				onRemoveReferenceImage={handleRemoveReferenceImage}
			/>

			{data.howItWork && (
				<>
					<HowItWorks
						data={data.howItWork}
						t={t}
						dropzoneOptions={dropzoneOptions}
						withDropzone={data.withDropZone}
						funnelLinkService={funnelLinkService}
						useEmailFlow={useEmailFlow}
					/>
					{data.toolsGrid && (
						<ToolsGrid
							data={data.toolsGrid.hero}
							t={tToolsGrid}
							withBackground={!data.toolsGrid.hideBackground}
						/>
					)}
				</>
			)}

			<Reviews t={tReviews} data={reviewsLanding} />

			{data.benefits && (
				<Benefits
					data={data.benefits}
					t={t}
					dropzoneOptions={dropzoneOptions}
					withDropzone={data.withDropZone}
					funnelLinkService={funnelLinkService}
					useEmailFlow={useEmailFlow}
				/>
			)}

			{data.faq && (
				<FAQ
					data={data.faq}
					t={t}
					funnelLinkService={funnelLinkService}
					withDropzone={data.withDropZone}
					dropzoneOptions={dropzoneOptions}
					useEmailFlow={useEmailFlow}
				/>
			)}
			{data.whyLift && (
				<WhyLift
					data={data.whyLift}
					t={t}
					dropzoneOptions={dropzoneOptions}
					withDropzone={data.withDropZone}
					funnelLinkService={funnelLinkService}
					useEmailFlow={useEmailFlow}
				/>
			)}
			{!data.hideToolsWidget && data.explore && (
				<ToolsWidget data={data.explore} t={tShared} />
			)}
			{data.formats && <Formats data={data.formats} t={tShared} />}
			{data.tryLift && <TryLift data={data.tryLift} t={tShared} />}
			{data.requiresEmailGate && <EmailPopup {...popupProps} />}
		</>
	);
};
